import React from 'react';
import { useTranslation } from 'react-i18next';

// Barre de progression d'une fabrication longue (plusieurs minutes).
//
// Extraite de StepTracking le 2026-09-15 : la generation du PDF client et
// l'envoi de test a l'imprimeur font le MEME travail (rendre chaque page en
// haute resolution) et publient desormais la meme forme de progression
// — { phase, done, total, updatedAt }. Deux barres distinctes auraient
// diverge au premier ajustement.

// Ces cles correspondent aux valeurs de `phase` envoyees par le serveur
// (backend/routes/orders.js) : ne pas les renommer, seul leur libelle est
// traduit (checkout:generationProgress.phases.*).
const PROGRESS_PHASE_KEYS = [
  'starting', 'cover', 'pages', 'photos',
  'preparation', 'ecriture', 'televersement', 'assembling', 'uploading', 'submitting'
];

// Phases dont on connait un DECOMPTE, et le nom de ce qu on compte.
//
// Le rendu par captures comptait des pages ; le rendu par impression
// (2026-09-19) n a plus de boucle page par page et compte des photos. Les
// deux alimentent la meme barre, avec le bon mot (identique en francais et
// en anglais, pas besoin de traduction).
const PHASES_CHIFFREES = { pages: 'pages', photos: 'photos' };

function GenerationProgress({ progress, label }) {
  const { t } = useTranslation('checkout');
  // Premier point de mesure de la phase "pages", garde d'un rendu a
  // l'autre. Il sert a deduire la CADENCE REELLE de la machine qui rend le
  // livre : elle n'a rien a voir en local et sur Render (CPU bien plus
  // lent), une constante en dur donnerait une promesse fausse la moitie du
  // temps. Les horodatages viennent du serveur (progress.updatedAt), pas de
  // l'horloge du navigateur : l'estimation reste juste meme si une reponse
  // de sondage arrive en retard.
  const paceRef = React.useRef(null);

  const phase = progress?.phase;
  const done = progress?.done || 0;
  const total = progress?.total || 0;
  const updatedAtMs = progress?.updatedAt ? Date.parse(progress.updatedAt) : NaN;

  const unite = PHASES_CHIFFREES[phase];

  // Une nouvelle phase repart de zero : melanger la cadence des photos et
  // celle des pages donnerait une estimation absurde.
  if (unite && done > 0 && paceRef.current?.phase !== phase && !Number.isNaN(updatedAtMs)) {
    paceRef.current = { phase, done, at: updatedAtMs };
  }

  if (!progress) return null;

  const hasCount = Boolean(unite) && total > 0;
  // Pourcentage REEL quand on le connait (pages rendues / total). Pour les
  // phases sans decompte, on n'invente pas de chiffre : barre indeterminee.
  const percent = hasCount ? Math.round((done / total) * 100) : null;

  // Duree restante : affichee seulement une fois qu'on a DEUX points de
  // mesure. Avant ca, le decompte "x / y pages" suffit — mieux vaut ne rien
  // annoncer qu'annoncer n'importe quoi.
  const pace = paceRef.current;
  let remainingLabel = null;
  if (hasCount && pace && pace.phase === phase && done > pace.done && !Number.isNaN(updatedAtMs)) {
    const secondsPerPage = (updatedAtMs - pace.at) / 1000 / (done - pace.done);
    if (secondsPerPage > 0) {
      const remainingMin = Math.round(((total - done) * secondsPerPage) / 60);
      remainingLabel = remainingMin >= 1
        ? t('generationProgress.remainingMinutes', { count: remainingMin })
        : t('generationProgress.remainingSeconds');
    }
  }

  const phaseLabel = PROGRESS_PHASE_KEYS.includes(phase)
    ? t(`generationProgress.phases.${phase}`)
    : '';

  return (
    <div className="gelato-progress">
      <div className="gelato-progress-head">
        <span>{phaseLabel || label || t('generationProgress.fallbackLabel')}</span>
        {hasCount && <span className="gelato-progress-count">{done} / {total} {unite}</span>}
      </div>
      <div className={`gelato-progress-bar ${percent === null ? 'is-indeterminate' : ''}`}>
        <div
          className="gelato-progress-fill"
          style={percent === null ? undefined : { width: `${percent}%` }}
        />
      </div>
      {remainingLabel && <p className="gelato-progress-note">{remainingLabel}</p>}
    </div>
  );
}

export default GenerationProgress;
